import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, User, CheckSquare, ArrowRight } from 'lucide-react';
import { useKiprData } from '@/lib/kiprData';
import { isLearnerKipPlayable, catalogStatusLabel } from '@/lib/catalogAvailability';

const ScenarioDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { kips, loading, clients, colleagues, isAdmin } = useKiprData();

  const kip = kips.find((k) => k.id === id);
  const canPlay = kip ? isLearnerKipPlayable(kip, clients, colleagues, isAdmin) : false;
  
  if (loading) {
    return <div className="text-[#555555]">Loading scenario...</div>;
  }

  if (!kip) {
    return <div>Scenario not found</div>;
  }

  const client = kip.client;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-poppins text-[#1A1A1A] mb-2">{kip.title}</h1>
        <div className="flex flex-wrap items-center gap-3 text-[#555555]">
          <div className="flex items-center space-x-2">
            <Clock size={20} />
            <span>Estimated time: {kip.duration}</span>
          </div>
          <span className="bg-[#DADADA] px-3 py-1 rounded-full text-sm">{kip.category}</span>
          {!canPlay && (
            <span className="bg-amber-100 text-amber-900 border border-amber-200 px-3 py-1 rounded-full text-xs font-semibold">
              {kip.availability !== 'available' ? catalogStatusLabel(kip.availability) : 'Coming soon'}
            </span>
          )}
        </div>
      </div>

      {/* Goal */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-3">Training Goal</h2>
        <p className="text-[#555555]">{kip.description}</p>
      </div>

      {client && (
        <>
          {/* Client Profile */}
          <div className="bg-[#F5F5F5] p-6 rounded-lg">
            <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-4 flex items-center">
              <User className="mr-2" size={24} />
              Client Profile
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">Basic Information</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Name:</span> {client.name}</p>
                  <p><span className="font-medium">Age:</span> {client.age} years old</p>
                  <p><span className="font-medium">Diagnosis:</span> {client.diagnosis}</p>
                  <p><span className="font-medium">Start Date:</span> {client.startDate}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">Learning Style</h3>
                <p className="text-sm text-[#555555]">{client.style}</p>
              </div>
            </div>
          </div>

          {/* Intake Summary */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-4">Intake Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Areas of Concern</h3>
                <ul className="space-y-2">
                  {client.concerns.map((concern, index) => (
                    <li key={index} className="text-sm text-[#555555] flex items-start">
                      <span className="text-[#121212] mr-2">•</span>
                      {concern}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Strengths</h3>
                <ul className="space-y-2">
                  {client.strengths.map((strength, index) => (
                    <li key={index} className="text-sm text-[#555555] flex items-start">
                      <span className="text-[#121212] mr-2">•</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Reinforcers</h3>
                <ul className="space-y-2">
                  {client.reinforcers.map((reinforcer, index) => (
                    <li key={index} className="text-sm text-[#555555] flex items-start">
                      <span className="text-[#121212] mr-2">•</span>
                      {reinforcer}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Triggers</h3>
                <ul className="space-y-2">
                  {client.triggers.map((trigger, index) => (
                    <li key={index} className="text-sm text-[#555555] flex items-start">
                      <span className="text-[#121212] mr-2">•</span>
                      {trigger}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Therapy Plan */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-4">Current Therapy Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Current Targets</h3>
                <div className="space-y-2">
                  {client.currentTargets.map((target, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckSquare size={16} className="text-[#555555]" />
                      <span className="text-sm text-[#555555]">{target}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">Techniques in Use</h3>
                <div className="space-y-2">
                  {client.techniques.map((technique, index) => (
                    <span
                      key={index}
                      className="inline-block bg-[#DADADA] text-[#1A1A1A] text-xs px-3 py-1 rounded-full mr-2 mb-2"
                    >
                      {technique}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Scenario Notes */}
          <div className="bg-[#DADADA] p-4 rounded-md">
            <p className="text-[#1A1A1A] italic">
              <strong>Scenario Notes:</strong> Pay attention to the client's non-verbal cues and be prepared to adjust your approach based on their responses. 
              Remember to use their preferred reinforcers and avoid known triggers during the interaction.
            </p>
          </div>
        </>
      )}

      {/* Sticky Footer with Begin Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#DADADA] p-4 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center">
            <button
              onClick={() => canPlay && navigate(`/player/${kip.id}`)}
              disabled={!canPlay}
              className={`px-12 py-4 rounded-lg shadow-lg font-semibold font-inter flex items-center space-x-3 ${
                canPlay
                  ? 'bg-[#121212] text-white hover:bg-[#2A2A2A] transition-colors'
                  : 'bg-[#ECECEC] text-[#888] cursor-not-allowed'
              }`}
            >
              <span>{canPlay ? 'Begin Kip' : 'Not available yet'}</span>
              {canPlay && <ArrowRight className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Add bottom padding to prevent content from being hidden behind sticky footer */}
      <div className="h-20"></div>
    </div>
  );
};

export default ScenarioDetail;